import React, { useEffect, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { cn } from '../../lib/utils'
import { buttonClass } from './Button'

export interface ActionMenuItem {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  /** Draw a divider above this item (e.g. to set a destructive action apart). */
  separated?: boolean
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  label?: string // accessible name of the trigger
  align?: 'left' | 'right'
}

/** "More" menu (⋯) for secondary and destructive actions that shouldn't sit in the toolbar. */
const ActionMenu: React.FC<ActionMenuProps> = ({ items, label = 'More actions', align = 'right' }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={buttonClass('secondary', 'icon')}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-50 mt-2 min-w-[12rem] py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {items.map((item, i) => (
            <React.Fragment key={item.label}>
              {item.separated && i > 0 && <div className="my-1 border-t border-gray-100 dark:border-gray-700" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => { setOpen(false); item.onClick() }}
                className={cn(
                  'w-full flex items-center gap-2.5 h-9 px-3 text-sm text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                  item.danger
                    ? 'text-loss hover:bg-gradient-to-r hover:from-rose-50 hover:to-pink-50 dark:hover:from-rose-900/20 dark:hover:to-pink-900/20'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20'
                )}
              >
                {item.icon && <span className="w-4 h-4 flex items-center justify-center shrink-0">{item.icon}</span>}
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export default ActionMenu
